'use client';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { ReactElement, Suspense, useMemo } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { Link, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { DetailPageHeader } from '@/components/detailPageHeader/detailPageHeader';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { useEvalRuns } from '@/hooks/queries/useEvaluations';
import { formatScore } from '@/utils/numberUtils';
import { parseParams } from '@/utils/searchParams';

import styles from './page.module.scss';

export const dynamic = 'force-dynamic';

type SearchParams = {
  eid?: string;
  rids?: string;
};

const columnHelper = createColumnHelper<Record<string, unknown>>();

function EvaluationComparePage(): ReactElement {
  const [ searchParams ] = useSearchParams();
  const { eid: qEvaluationId = '', rids: qRunIds = '' } = parseParams<SearchParams>( searchParams );

  const selectedRunIds = useMemo(
    () => qRunIds.split( ',' ).map( id => id.trim() ).filter( Boolean ),
    [ qRunIds ]
  );

  const { data, isLoading } = useEvalRuns( qEvaluationId );
  const evaluation = data?.evaluation;

  // The compare view pivots the runs table: one row per metric, one column per
  // selected run. Preserve the order the runs were selected in (the `rids` param).
  const selectedRuns = useMemo( () => {
    if ( !data?.runs ) return [];
    const byId = new Map( data.runs.map( r => [ r.run.id, r ] ) );
    return selectedRunIds.map( id => byId.get( id ) ).filter( ( r ): r is NonNullable<typeof r> => !!r );
  }, [ data?.runs, selectedRunIds ] );

  // Union of metrics across the selected runs, in first-seen order, plus a
  // per-run lookup of each metric's average score keyed by provider|name.
  const { metrics, scoresByRun } = useMemo( () => {
    const metricMap = new Map<string, string>();
    const scores: Record<string, Record<string, number | undefined>> = {};

    for ( const { run, details } of selectedRuns ) {
      scores[ run.id ] = {};
      for ( const metric of details?.metrics ?? [] ) {
        const key = `${ metric.metric_provider_id }|${ metric.metric_name }`;
        if ( !metricMap.has( key ) ) metricMap.set( key, metric.metric_name );
        scores[ run.id ][ key ] = metric.average_score;
      }
    }

    return {
      metrics: [ ...metricMap.entries() ].map( ( [ key, name ] ) => ( { key, name } ) ),
      scoresByRun: scores
    };
  }, [ selectedRuns ] );

  const tableData = useMemo<Record<string, unknown>[]>( () => {
    return metrics.map( ( { key, name } ) => {
      const row: Record<string, unknown> = { _mkey: key, metric: name };
      for ( const { run } of selectedRuns ) {
        row[ run.id ] = formatScore( scoresByRun[ run.id ]?.[ key ] );
      }
      return row;
    } );
  }, [ metrics, selectedRuns, scoresByRun ] );

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>( () => {
    const metricColumn = columnHelper.accessor( 'metric', {
      id: 'metric',
      header: 'Metric',
      size: 220,
      cell: info => (
        <span className={ styles.metricCell }>
          <Icon icon={ AiOutlineInfoCircle } size="sm" />
          <span className={ styles.metricName }>{ info.getValue() as string }</span>
        </span>
      )
    } );

    const runColumns = selectedRuns.map( ( { run }, index ) => columnHelper.accessor( run.id, {
      id: run.id,
      size: 150,
      header: () => (
        <span className={ styles.runHeader }>
          <span className={ styles.runLabel }>{ `Run ${ index + 1 }` }</span>
          <Link
            className={ styles.runLink }
            to={ `/evaluations/results?eid=${ qEvaluationId }&rid=${ run.id }` }
            title={ run.id }
          >{ run.id }
          </Link>
        </span>
      ),
      cell: info => info.getValue() as string
    } ) );

    return [ metricColumn, ...runColumns ];
  }, [ selectedRuns, qEvaluationId ] );

  const table = useNativeTable<Record<string, unknown>>( {
    data: tableData,
    columns,
    enableSorting: false,
    getRowId: row => row._mkey as string,
    initialState: {
      columnPinning: { left: [ 'metric' ] }
    }
  } );

  const renderContent = (): ReactElement => {
    if ( selectedRuns.length === 0 ) {
      return (
        <p className={ styles.placeholder } data-testid="compare-empty">
          No runs selected to compare. Go back and select two or more runs.
        </p>
      );
    }

    if ( metrics.length === 0 ) {
      return (
        <p className={ styles.placeholder } data-testid="compare-no-metrics">
          The selected runs have no metric results to compare yet.
        </p>
      );
    }

    return (
      <div className={ styles.tableWrap } data-testid="compare-table">
        <NativeTable table={ table } pagination={ false } />
      </div>
    );
  };

  return (
    <div className="page">
      <ToastContainer />
      { isLoading ? (
        <Loader />
      ) : (
        <>
          <DetailPageHeader
            backTo={ `/evaluations/runs?eid=${ qEvaluationId }` }
            backLabel={ `Back to ${ evaluation?.name ?? 'Evaluation' }` }
            backTestId="compare-back"
            title={ (
              <>
                { `Comparing ${ selectedRuns.length } run${ selectedRuns.length === 1 ? '' : 's' } of ` }
                <span className={ styles.titleEval }>{ evaluation?.name }</span>
              </>
            ) }
          />

          { renderContent() }
        </>
      ) }
    </div>
  );
}

export default function Page(): ReactElement {
  return (
    <Suspense fallback={ <Loader /> }>
      <EvaluationComparePage />
    </Suspense>
  );
}
